import { RenderPlan } from "@tento-lona/sheets/platform";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { SheetRow } from "@tento-lona";
import { nonnull } from "@tento-core/assert";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import { FrameMarks } from "@tento-ui/frame-marks";
import type { CellDirective, CommitBundle } from "./commit-bundle.ts";
import type { CellRefereeVerdict, RefereeVerdict } from "./commit-referee.ts";
import type { LiveSceneRunner } from "./live-scene-runner.ts";
import { prepareColumnCommit } from "./column-commit-preparation.ts";
import type { ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";
import { logCommitOps, reconcileFullPass } from "./full-pass-reconcile.ts";
import { makeFullPassHost } from "./full-pass-apply.ts";
import { applyAndRefereeCells } from "./full-pass-referee.ts";
import { logColumnShape, verifyPlannedHeights } from "./full-pass-verify.ts";

/**
 * The two column passes of `DefaultBinder.onBind`, as functions over a
 * narrow port to the binder's state.
 *
 * `onBind` selects the column's resident rows, evicts the rows that left and
 * then runs ONE of these:
 * - `resizeOnly`: the cells already show the target rows in order; each is
 *   re-laid out for its plan (`applyPlanResize`) and re-placed. No structure
 *   change, no content rebind.
 * - `full`: reconcile the cells against the target rows by stable row
 *   identity, compile and apply the commit bundle (mount / recycle / remove,
 *   then per cell rebind / reflow / skip, with the membership-only keep),
 *   referee the dark scene's directives, place the cells, advance the
 *   retained scene and verify every visible cell's planned height.
 *
 * The per-cell appliers (`applyPlanResize`, `applyPlanFullRebind`,
 * `applyPlanReflow`) and all retained state stay on the binder; the port is
 * how a pass reaches them, so a pass holds no state of its own.
 */

export type { ApplyContext, ColumnBindPort, ColumnPassInput } from "./column-bind-port.ts";

/** The resize-only pass: the cells already show the target rows; re-lay each out for its plan. */
function resizeOnlyPass(port: ColumnBindPort, input: ColumnPassInput): void {
  const { sheet, defaultColumn, $cells, residency, widthPx, now, applyContext, placeCells } = input;
  // Resolve each cell's row by its stable wire-string identity, keeping the
  // cell↔row pairing explicit. `$cell.cellId.row` is a `RowIdentity` (the
  // `row.id.toString()` wire/RowKey string), NOT a `RowLocalId` — the old
  // `sheet.tree.byId.get(cid.row as RowLocalId)` cast the string to the
  // wrong key type, so the RowLocalId-keyed map missed for EVERY cell. With
  // no rows resolved, `makeForRows` produced zero plans and the per-cell
  // resize never ran, leaving explicit-width content (canvas/series charts)
  // painted at the pre-resize width — the "cells don't stretch on resize"
  // gap. Match by wire string the same way the full-rebind reconcile below
  // does (`rid` attribute ↔ `row.id.toString()`); fall back to the `rid`
  // attribute when `cellId` is absent.
  const cellRows: { $cell: SheetCell; row: SheetRow.Joined.Handle }[] = [];
  for (const $cell of $cells) {
    const wire = $cell.cellId?.row?.toString() ?? $cell.getAttribute("rid");
    if (!wire) continue;
    const serial = $cell.getAttribute("rsid");
    const row = serial == null ? residency.rowForWire(wire) : residency.rowsBySerial.get(serial);
    if (row) cellRows.push({ $cell, row });
  }

  // Compute all render plans at once (pure computation)
  const context = port.createPlanContext(
    sheet,
    defaultColumn,
    widthPx,
    now,
  );
  const plans = RenderPlan.makeForRows(
    context,
    cellRows.map((cr) => cr.row),
    port.previousRenderState,
    "resize-only",
    port.subcolumnCache,
  );

  // Apply all plans (plans[i] corresponds to cellRows[i] by construction).
  const touchedRowIds: RowLocalId[] = [];
  for (let i = 0; i < plans.length; i++) {
    if (!port.applyCurrent(applyContext)) return;
    const plan = nonnull(plans[i], "resize: plans[i]");
    if (!plan.layout) continue;
    const cellRow = nonnull(cellRows[i], "resize: plans[i] has its cellRows[i]");

    port.applyPlanResize(
      cellRow.$cell,
      plan,
      cellRow.row,
      sheet,
      defaultColumn,
      widthPx,
      now,
      applyContext,
    );
    touchedRowIds.push(cellRow.row.id);
  }
  // Flip prerequisite: prune the EXACT rows this resize repainted from the
  // retained scene. `plans[i] ↔ cellRows[i] ↔ row.id` is exact (cell↔row
  // resolved by wire string above), so no column-wide fallback is needed.
  if (!port.applyCurrent(applyContext)) return;
  port.liveScene.invalidate(defaultColumn, touchedRowIds);
  placeCells();
}

/**
 * The full pass: reconcile, compile and apply the commit bundle, referee the
 * dark scene, place, advance the retained scene and verify planned heights.
 * `membershipOnly`: the pass only moved the viewport (F10 keeps).
 */
function fullPass(port: ColumnBindPort, input: ColumnPassInput, membershipOnly: boolean): void {
  const { sheet, defaultColumn, rows, applyContext, placeCells } = input;
  const reconciled = reconcileFullPass(port, input);
  const { changesets, oldRowIds, targetRowIds, plans, bindings } = reconciled;

  // The scene we built this pass (retained as the next diff's base ONLY after a
  // successful apply — never past a throw).
  let builtScene: Option<LiveSceneRunner.Built> = null;

  // Diagnostic capture (the debug override's breadcrumb): observability-only state
  // for the end-of-pass `render-next/column-shape` breadcrumb. Set at the
  // referee / scene-promote sites below; read only inside the gated breadcrumb.
  // These are pure captures of values already computed — no behavioral effect.
  let sceneUsedThisPass = false;
  let commitVerdict: Option<RefereeVerdict> = null;
  let cellVerdict: Option<CellRefereeVerdict> = null;

  // Un-fused commit: compile the structural + dispatch decisions into a pure
  // `CommitBundle`, then execute it against a decision-free `CommitHost`. The
  // host methods mirror the retired fused inline blocks byte-for-byte; the
  // observable sequence is identical.
  let bundle: Option<CommitBundle> = null;
  // Per-cell directives, classified against the SELECTED structure + the
  // scene diff once both exist (dark: refereed, honored only when
  // the SDK 2.0 switch). `null` when there is no built scene this pass.
  let directives: Option<readonly CellDirective[]> = null;
  const apply = makeFullPassHost(port, input, reconciled, membershipOnly);

  const prepareStart = FrameMarks.begin();
  const preparedCommit = prepareColumnCommit({
    sceneInput: { sheet, column: defaultColumn, rows, plans, bindings },
    positionalInput: { changes: changesets, oldRowIds, targetKeys: targetRowIds },
    sceneStation: port.liveScene,
    referees: port.referees,
    currentFlags: () => port.renderFlags(),
  });
  FrameMarks.end("binder.prepare", prepareStart, rows.length, 0);
  builtScene = preparedCommit.builtScene;
  bundle = preparedCommit.bundle;
  directives = preparedCommit.directives;
  apply.setDirectives(directives);
  sceneUsedThisPass = preparedCommit.sceneUsed;
  commitVerdict = preparedCommit.commitVerdict;

  port.reconciler.applyStructure(bundle, apply.host);

  logCommitOps(port, input, reconciled);

  cellVerdict = applyAndRefereeCells(port, input, apply, bundle, directives);

  if (!port.applyCurrent(applyContext)) return;
  placeCells();
  if (!port.applyCurrent(applyContext)) return;

  // prevScene commit — advance the retained scene ONLY now that structure +
  // cells applied cleanly (never past a throw). The post-apply DOM state equals
  // this scene's rows, so it is the correct base for the next bind's diff +
  // `compileSceneCommit` precondition.
  if (builtScene != null) {
    if (!port.liveScene.commitScene(defaultColumn, builtScene.scene)) {
      // A reentrant bind issued newer paint while this apply was running.
      // Its scene owns the next diff; stale per-row paint evidence cannot.
      port.previousRenderState.clear();
      // Modes own static/span cleanup until the next transition or dispose.
      port.renderCursor()?.reset();
      const contextEpoch = port.contextEpoch();
      queueMicrotask(() => {
        if (port.contextEpoch() === contextEpoch) port.handle.rebind.rebindColumn(ColumnIndex.Key.fromIndex(defaultColumn.offset));
      });
      return;
    }
  }

  port.updatePreviousState(plans);

  if (!verifyPlannedHeights(port, input, reconciled)) return;

  logColumnShape(port, input, reconciled, { bundle, directives, sceneUsedThisPass, commitVerdict, cellVerdict });
}

/** The binder's column passes (see the module note). */
export namespace ColumnBindPasses {
  export const resizeOnly = resizeOnlyPass;
  export const full = fullPass;
}
